import React, { useState, useEffect, useRef, useCallback } from "react";
import { Label, Popup, Button, Grid, Segment, Transition } from "semantic-ui-react";
import { toast } from "react-toastify";
import { validate } from "uuid";

import { ITableAppRender } from "../../components/TableApp";
import TemplateTable from "../../components/TemplateTable";
import ConsultReseller from "./ConsultReseller";
import CadastroPessoa from "../../components/CadastroPessoa";

import ResellersRepository, { IResellerListModel } from "../../Repositories/ResellersRepository";
import { ISearch, TSearch } from "../../components/Search";
import { ISearchResponse } from "../../interfaces/Search"
import EditarPessoa from "../../components/EditarPessoa";

import errors from "../../Utils/ExtractErrors";
import { GetUser } from "../../services/auth";
import { format } from "date-fns";
import { IFields } from "../../components/Search/FilterBuilder";
import { eStatus } from "../../interfaces";
import ConsultDetails from "../Consults/ConsultDetails";
import { CSVLink } from "react-csv";

const Resellers: React.FC = () => {

    const [list, setList] = useState<IResellerListModel[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [predefined, setPredefined] = useState<boolean>(false);
    const [visible, setVisible] = useState<boolean>(false);

    const refSearch = useRef<ISearch>({ searchString: "", type: "simples" });
    const refRecordsByPage = useRef<number>(20);
    const refPageNumber = useRef<string | number | undefined>(1);
    const refTotalRecords = useRef<number>(0);
    const refTotalPages = useRef<number>(1);

    const date = new Date();
    const actualYear = date.getFullYear();
    const actualMonth = date.getMonth() + 1;
    const actualDay = date.getDate();
    const { role, refId } = GetUser();

    const onChangeRecords = (value: number) => {
        refRecordsByPage.current = value;
        load();
    }

    const onPageChange = (pageNumber: string | number | undefined) => {
        refPageNumber.current = pageNumber;
        load();
    }

    const onClickPredefined = () => {
        setPredefined(!predefined);
        setVisible(!visible);
    }

    const load = useCallback(async () => {

        setLoading(true);
        try {
            let response: ISearchResponse<IResellerListModel>;

            if (refSearch.current.type === "simples") {
                response = await ResellersRepository.List({
                    search: refSearch.current.searchString,
                    pageNumber: refPageNumber.current,
                    recordsByPage: refRecordsByPage.current
                });
            } else {
                response = await ResellersRepository.List({
                    filter: refSearch.current.searchString,
                    pageNumber: refPageNumber.current,
                    recordsByPage: refRecordsByPage.current
                });
            }

            refTotalRecords.current = response.totalRecords;
            refTotalPages.current = response.totalPages;

            setList(response.list);
        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }

            const error = errors.getAllErrors(err.response.data);

            if (error !== undefined)
                toast(error, { position: "top-center", type: "error" });
        }
        setLoading(false);
    }, []);

    const onSearch = ({ searchString, type }: ISearch) => {
        refSearch.current.searchString = searchString;
        refSearch.current.type = type;
        setPredefined(false);
        setVisible(false);
        load();
    }

    useEffect(() => { load() }, [load]);

    const setStatus = (devedor: number, status: eStatus, data: string) => {
        if (devedor === 2) {
            return <Popup
                trigger={<Label ribbon icon="thumbs up" color="orange" content="NEG" />}
                content={`Data de cadastro: ${format(new Date(data), "dd/MM/yyyy")}`}
                wide
            />
        }
        if (devedor === 1) {
            return <Popup
                trigger={<Label ribbon icon="user times" color="red" content="DEV" />}
                content={`Data de cadastro: ${format(new Date(data), "dd/MM/yyyy")}`}
                wide
            />
        }
        else {
            switch (status) {
                case eStatus.Inativo:
                    return <Popup
                        trigger={<Label ribbon icon="times" color="grey" content="INA" />}
                        content={`Data de cadastro: ${format(new Date(data), "dd/MM/yyyy")}`}
                        wide
                    />
                case eStatus.Ativo:
                    return <Popup
                        trigger={<Label ribbon icon="check" color="pink" content="ATI" />}
                        content={`Data de cadastro: ${format(new Date(data), "dd/MM/yyyy")}`}
                        wide
                    />
                case eStatus.Pendente:
                    return <Popup
                        trigger={<Label ribbon icon="clock" color="orange" content="PEN" />}
                        content={`Data de cadastro: ${format(new Date(data), "dd/MM/yyyy")}`}
                        wide
                    />
                case eStatus.PreCadastro:
                    return <Popup
                        trigger={<Label ribbon icon="thumbs up" color="green" content="PRE" />}
                        content={`Data de cadastro: ${format(new Date(data), "dd/MM/yyyy")}`}
                        wide
                    />
                case eStatus.Analise:
                    return <Popup
                        trigger={<Label ribbon icon="search" color="yellow" content="ANL" />}
                        content={`Data de cadastro: ${format(new Date(data), "dd/MM/yyyy")}`}
                        wide
                    />
                case eStatus.Reprovado:
                    return <Popup
                        trigger={<Label ribbon icon="dont" color="black" content="RPV" />}
                        content={`Data de cadastro: ${format(new Date(data), "dd/MM/yyyy")}`}
                        wide
                    />
                default:
                    return "";
            }

        }
    }

    const tableRender: ITableAppRender[] = [
        {
            header: "Status",
            content: (a: IResellerListModel) => setStatus(a.devedor, a.status, a.data),
            textAlign: "left",
            width: 1
        },
        {
            header: "CPF",
            content: (a: IResellerListModel) => a.cpf,
            width: 2
        },
        {
            header: "Nome",
            content: (a: IResellerListModel) => a.nome,
            width: 3
        },
        {
            header: "Representante",
            content: (a: IResellerListModel) => a.representante,
            width: 3
        },
        {
            header: "Cidade",
            content: (a: IResellerListModel) => a.cidade,
            width: 2
        },
    ];

    const fields: IFields[] = [
        { label: "G. Regional", name: "Representante.GerenteSetor.GerenteRegional.Nome", type: "string", roles: ["Admin", "Operador"] },
        { label: "G. Setor", name: "Representante.GerenteSetor.Nome", type: "string", roles: ["Admin", "Operador", "GerenteR"] },
        { label: "Representante", name: "Representante.Nome", type: "string", roles: ["Admin", "Operador", "GerenteR", "GerenteS"] },
        { label: "CPF", name: "Cpf", type: "string", roles: ["Admin", "Operador"] },
        { label: "Revendedor", name: "Nome", type: "string", roles: ["Admin", "Operador", "GerenteR", "GerenteS", "Representante"] },
        { label: "Cidade", name: "Cidade", type: "string", roles: ["Admin", "Operador", "GerenteR", "GerenteS"] },
        { label: "Rua", name: "Logradouro", type: "string", roles: ["Admin", "Operador"] },
        { label: "Celular", name: "Celular", type: "string", roles: ["Admin", "Operador"] },
        { label: "Data Cadastro", name: "Data", type: "string", roles: ["Admin", "Operador"] },
        {
            label: "Status",
            name: "Status",
            type: "enum",
            values: [
                { key: "INA", text: "INATIVO", value: 0, label: { icon: "times", color: "grey", content: "INA" } },
                { key: "ATI", text: "ATIVO", value: 1, label: { icon: "check", color: "pink", content: "ATI" } },
                { key: "PEN", text: "PENDENTE", value: 2, label: { icon: "clock", color: "orange", content: "PEN" } },
                { key: "PRE", text: "PRE-CAD.", value: 3, label: { icon: "thumbs up", color: "green", content: "PRE" } },
                { key: "ANL", text: "ANÁLISE", value: 4, label: { icon: "search", color: "yellow", content: "ANL" } },
                { key: "RPV", text: "REPROVADO", value: 5, label: { icon: "dont", color: "black", content: "RPV" } },
            ],
            roles: ["Admin", "Operador", "GerenteR", "GerenteS", "Representante"]
        },
        {
            label: "Devedor",
            name: "Devedor",
            type: "enum",
            values: [
                { key: "NÃO", text: "NÃO", value: 0, label: { icon: "user", color: "green", content: "NDV" } },
                { key: "SIM", text: "SIM", value: 1, label: { icon: "user times", color: "red", content: "DEV" } },
                { key: "NEG", text: "NEG", value: 2, label: { icon: "thumbs up", color: "orange", content: "NEG" } },
            ],
            roles: ["Admin", "Operador", "GerenteR", "GerenteS", "Representante"]
        }
    ];

    const headers = [
        "ID", "DATA CAD.", "STATUS", "DEVEDOR", "CPF", "NOME", "CELULAR", "REPRESENTANTE", "ID REPRESENTANTE", "CIDADE",
        "ESTADO", "REF. CONSULTA"
    ]
    const csvReport = {
        data: list.map(row => Object.values(row)),
        headers: headers,
        filename: 'Revendedores.csv'
    };

    const predefinedRegion = (
        <Grid>
            <Grid.Column>
                <Segment color="pink">
                    <Grid columns="equal">
                        <Grid.Row>
                            <Grid.Column>
                                <Popup hoverable content='Revendedores pendentes para análise de documneto.' position="bottom center" trigger={
                                    <Button
                                        icon="clock"
                                        fluid
                                        color="grey"
                                        content="Pendente"
                                        onClick={() => onSearch({ searchString: "STATUS == 2", type: "avancado" })}
                                    />
                                }
                                />
                            </Grid.Column>
                            <Grid.Column>
                                <Popup hoverable content='Revendedores para análise interna.' position="bottom center" trigger={
                                    <Button
                                        icon="search"
                                        fluid
                                        color="grey"
                                        content="Análise"
                                        onClick={() => onSearch({ searchString: "STATUS == 4", type: "avancado" })}
                                    />
                                }
                                />
                            </Grid.Column>
                            <Grid.Column>
                                <Popup hoverable content='Revendedores que contém débito.' position="bottom center" trigger={
                                    <Button
                                        icon="user times"
                                        fluid
                                        color="grey"
                                        content="Devedores"
                                        onClick={() => onSearch({ searchString: "DEVEDOR == 1", type: "avancado" })}
                                    />
                                }
                                />
                            </Grid.Column>
                            <Grid.Column>
                                <Popup hoverable content='Revendedores que possuem negociação em aberto.' position="bottom center" trigger={
                                    <Button
                                        icon="thumbs up"
                                        fluid
                                        color="grey"
                                        content="Negociação"
                                        onClick={() => onSearch({
                                            searchString: "DEVEDOR == 2",
                                            type: "avancado"
                                        })}
                                    />
                                }
                                />
                            </Grid.Column>
                            <Grid.Column>
                                <Popup hoverable content='Cadastros pré-aprovados que vão expirar hoje as 00:00' position="bottom center" trigger={
                                    <Button
                                        icon="trash alternate"
                                        fluid
                                        color="grey"
                                        content="Expirar"
                                        onClick={() => onSearch({
                                            searchString: `STATUS == 3 AND DATA < "${actualYear}-${actualMonth - 1}-${actualDay}"`,
                                            type: "avancado"
                                        })}
                                    />
                                }
                                />
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row textAlign="right">
                            <Grid.Column>
                                <Button
                                    size="tiny"
                                    color="red"
                                    icon="trash"
                                    floated="right"
                                    labelPosition="left"
                                    content="Limpar filtro"
                                    onClick={() => onSearch({ searchString: "", type: "simples" })}
                                />
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Segment>
            </Grid.Column>
        </Grid>
    );

    return (
        <TemplateTable
            title="Revendedores"
            icon="street view"
            insert={<>
                <Grid centered={false}>
                    <Grid.Column textAlign="right">
                        {
                            role === "Admin" || role === "Operador" || role === "GerenteS" ?
                                <>
                                    <CSVLink
                                        separator={";"}
                                        {...csvReport}
                                    >
                                        <Popup content='Download Excel' position="bottom center" trigger={
                                            <Button
                                                color="green"
                                                icon="download"
                                            />
                                        } />
                                    </CSVLink>
                                    <Button
                                        content={!predefined ? "Mostrar filtros" : "Ocultar filtros"}
                                        labelPosition="left"
                                        icon={!predefined ? "eye" : "eye slash"}
                                        color={!predefined ? "blue" : "black"}
                                        onClick={onClickPredefined}
                                    />
                                </>
                                :
                                //undefined //2023-03-01
                                //role === "Representante" ? <ConsultReseller onSuccess={load} /> : undefined
                                //2023-03-01 João
                                role === "Representante" ? <CadastroPessoa url="/revendedor" rpId={refId} status="4" onSuccess={load} /> : undefined
                        }
                    </Grid.Column>
                </Grid>
            </>
            }
            predefinedFilters={
                <Transition visible={visible} animation="fade down" duration={300}>
                    {predefinedRegion}
                </Transition>
            }
            searchPlaceHolder={"Nome"}
            onSearch={onSearch}
            filterFields={fields}

            tableLoading={loading}
            tableRender={tableRender}
            tableList={list}
            tableActions={(data: IResellerListModel, index: number) => [
                <EditarPessoa id={data.id || ""} url={`/revendedor/${data.id}`} onSuccess={load} />,
                role === "Admin" && validate(data.consulta) ? <ConsultDetails idConsulta={data.consulta} onSuccess={load} /> : <></>
            ]}
            tablePagination={{
                currentPage: refPageNumber.current,
                totalRecords: refTotalRecords.current,
                totalPages: refTotalPages.current,
                onPageChange: onPageChange
            }}
            tableOnChangeRecords={onChangeRecords}
        />
    );
};

export default Resellers;
